import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FlaskConical } from "lucide-react";
import { COMMODITY_DATA, SPECS, CONTACT_EMAIL } from "../mock";
import SampleRequestDialog from "../components/SampleRequestDialog";
import { useCupping } from "../hooks/useCupping";

export default function SpecSheetPage() {
  const { commodity, lot } = useParams();
  const [sampleOpen, setSampleOpen] = useState(false);
  const cupping = useCupping()[lot];
  const data = COMMODITY_DATA[commodity];
  const item = data?.range.items.find((i) => i.id === lot);
  const spec = SPECS[lot];
  if (!data || !item || !spec) return <Navigate to="/trading/coffee" replace />;

  const origin = data.origins.items.find((o) => o.region === spec.originRegion) || data.origins.items[0];

  return (
    <main className="bg-[#ece2cf] min-h-screen pt-28 md:pt-32 pb-24 print:bg-white print:pt-0 print:pb-0" data-testid={`spec-page-${lot}`}>
      <div className="mx-auto max-w-[900px] px-4 md:px-10">
        <div className="no-print flex items-center justify-between gap-4 mb-8">
          <Link to={`/trading/${commodity}`} className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase font-semibold text-[#1f4a33]" data-testid="spec-back">
            <ArrowLeft size={16} /> Back to {data.label}
          </Link>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setSampleOpen(true)} data-testid="spec-sample" className="inline-flex items-center gap-2 border border-[#1f4a33]/30 text-[#1f4a33] px-5 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold hover:border-[#1f4a33] transition-colors">
              <FlaskConical size={15} /> Request sample
            </button>
            <button type="button" onClick={() => window.print()} data-testid="spec-download" className="inline-flex items-center gap-2 bg-[#1f4a33] text-[#f6f1e7] px-5 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors">
              <Download size={15} /> Download PDF
            </button>
          </div>
        </div>

        <article className="spec-sheet bg-[#faf6ec] rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-40px_rgba(16,39,26,0.5)] print:shadow-none print:rounded-none">
          <header className="bg-[#10271a] text-[#f6f1e7] px-8 md:px-12 py-8 flex items-start justify-between gap-6 print:bg-[#10271a]">
            <div>
              <p className="text-[#b08a5f] text-[11px] tracking-[0.25em] uppercase">Lot specification · {data.label}</p>
              <h1 className="font-display text-4xl sm:text-5xl font-light leading-[1.0] mt-3">{item.name}</h1>
              <p className="mt-3 text-[#f6f1e7]/75">{item.origin}</p>
            </div>
            <div className="text-right shrink-0">
              <img src="/logo-white.png" alt="Leslie West Africa Trading Company" className="h-12 w-auto ml-auto" />
              <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#f6f1e7]/60">Lot code</p>
              <p className="font-mono text-sm" data-testid="spec-code">{spec.code}</p>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] print:grid-cols-[1.1fr_1fr]">
            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[260px]">
              <img src={item.image} alt={item.name} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="px-8 md:px-10 py-8">
              <p className="text-[#7c5230] text-[11px] tracking-[0.2em] uppercase">Summary</p>
              <p className="mt-3 text-[#10271a] leading-relaxed">{spec.summary}</p>
              {cupping && (
                <div className="mt-5 rounded-2xl bg-[#10271a] text-[#f6f1e7] p-4" data-testid="spec-cupping">
                  <div className="flex items-baseline justify-between"><p className="text-[#b08a5f] text-[10px] tracking-[0.2em] uppercase">Latest cupping</p><p><span className="font-display text-2xl">{cupping.score}</span> <span className="text-[#f6f1e7]/60 text-xs">{cupping.scale}</span></p></div>
                  <p className="mt-2 text-sm text-[#f6f1e7]/85 leading-relaxed">{cupping.notes}</p>
                  <p className="mt-2 text-[10px] text-[#f6f1e7]/50 tracking-[0.1em] uppercase">{cupping.cupper} · {cupping.cupped_on}</p>
                </div>
              )}
              <p className="mt-5 text-[#7c5230] text-[11px] tracking-[0.2em] uppercase">Flavour profile</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.flavours.map((f) => (
                  <span key={f} className="text-[11px] tracking-[0.08em] uppercase text-[#1f4a33] border border-[#1f4a33]/25 px-3 py-1 rounded-full">{f}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="px-8 md:px-12 py-8 grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-x-12 gap-y-1 border-t border-[#10271a]/10" data-testid="spec-rows">
            {[["Region", origin.region], ["Altitude", origin.altitude], ["Varietal / species", origin.varietal], ["Process", origin.process], ...spec.rows].map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-6 py-3 border-b border-[#10271a]/8 text-sm">
                <span className="text-[#7c5230] tracking-[0.12em] uppercase text-[11px] pt-0.5 shrink-0">{label}</span>
                <span className="text-[#10271a] font-medium text-right">{value}</span>
              </div>
            ))}
          </div>

          <footer className="px-8 md:px-12 py-6 bg-[#ece2cf]/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-[#5e6b61]">
            <p>Leslie West Africa Trading Company · Trade desk: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1f4a33]">{CONTACT_EMAIL}</a></p>
            <p>Issued {spec.issued} · Indicative; pre-shipment samples confirm final grading.</p>
          </footer>
        </article>
      </div>
      <SampleRequestDialog open={sampleOpen} onOpenChange={setSampleOpen} commodity={commodity} lot={item} />
    </main>
  );
}
